/**
* @name: useMsgOpenCfg
* @description：消息打开配置 hooks（点击消息按消息定义配置的「内容来源」打开对应界面）
*   配置链路：消息定义端 msg-open-config（打开方式 + 内容来源）整体存入
*   messageInfo configList[].tempConfigs（含 openType/pageFrom/tableCode/pageCode/moduleCode/
*   pageUrl/winTitle/winWidth/winHeight），message 服务发站内信时渲染占位符后整体存入
*   inBoxMessage.content，查询接口透出为 messageSetting.tempConfigs；
*   点击消息时解析该配置分流：
*   - flowForm 流程表单：参照流程已办口径打开 /flowList/simplFlowDeal（pageFrom=haveDone 只读查看），
*     tableCode 取 formatContent.tableCode，缺失时回退配置的领域tableCode；
*   - page 界面：页签打开 /vue3Des/showPage/{pageCode}（页签名=配置标题/消息标题）；
*   - module 部件：openSubComPage 按配置 pageCode+moduleCode 弹抽屉/窗口（openType 决定形态）；
*   - pageUrl 外部链接：newWindow 弹窗 / newDrawer 抽屉 内嵌 iframe（复用 msg-detail-win）；
*   未配置内容来源时回退 openType：routerPath 路由跳转、notClick 吞掉点击；
*   均未配置或配置不完整返回 false，由调用方走默认逻辑（流程消息页签 / 消息详情抽屉）
*/
import {useOpenSubPage} from '@/main-page/gridCellCtlShow/hooks/useOpenSubPage';
import {StorageUtils} from 'xmgoing-utils';

export function useMsgOpenCfg({router, winTemConfig, msgDetailFlag}: any = {}) {

  const {openSubComPage} = useOpenSubPage();

  /**
   * 解析消息携带的打开配置：messageSetting.tempConfigs（后端=入库时渲染的完整 tempConfigs JSON）
   */
  const parseMsgOpenCfg = function (record: any): any {
    let cfg = record && record.messageSetting && record.messageSetting.tempConfigs;
    if (typeof cfg === 'string' && cfg) {
      try {
        cfg = JSON.parse(cfg);
      } catch (e) {
        return null;
      }
    }
    return cfg && typeof cfg === 'object' ? cfg : null;
  };

  /**
   * 消息标题：与消息列表展示口径一致（titleContentPre : title），页签/弹窗命名用
   */
  const getMsgTitle = function (record: any): string {
    let title = (record && record.title) || '';
    let content: any = record && record.content;
    if (typeof content === 'string' && content) {
      try {
        content = JSON.parse(content);
      } catch (e) {
        content = null;
      }
    }
    if (content && content.titleContentPre) {
      title = content.titleContentPre + ' : ' + title;
    }
    return title;
  };

  /**
   * 内容来源配置是否可执行（pageFrom 存在且必填字段齐全）
   */
  const hasContentSource = function (cfg: any): boolean {
    if (!cfg || !cfg.pageFrom) {
      return false;
    }
    if (cfg.pageFrom === 'page') {
      return !!cfg.pageCode;
    }
    if (cfg.pageFrom === 'module') {
      return !!cfg.pageCode && !!cfg.moduleCode;
    }
    if (cfg.pageFrom === 'pageUrl') {
      return !!cfg.pageUrl;
    }
    return cfg.pageFrom === 'flowForm';
  };

  /**
   * 流程表单页签：参照流程已办（pageFrom=haveDone 只读查看）；
   * BPM 流程（msgFlowType=flow 且带流程实例）与待办打开同构仅改只读口径，其余走简易流程路径
   */
  const openFlowFormTab = function (record: any, cfg: any): boolean {
    const formatContent = (record && record.formatContent) || {};
    const flowId = formatContent.flowId || '';
    const formDataId = formatContent.id || formatContent.businessKey || '';
    // 流程标识缺失时无表单可开，回退默认逻辑（消息详情抽屉）
    if (!flowId && !formDataId) {
      return false;
    }
    const tableCode = formatContent.tableCode || cfg.tableCode || '';
    const flowTitle = (getMsgTitle(record) || '流程消息').slice(0, 25);
    if (formatContent.msgFlowType === 'flow' && formatContent.processInstanceId) {
      router.push({
        path: '/flowBpmList/flowList/simplFlowDeal',
        query: {
          recordId: String(flowId),
          tableCode: String(tableCode),
          formDataId: String(formDataId),
          pageFrom: 'haveDone',
          processInstanceId: String(formatContent.processInstanceId || ''),
          taskId: String(formatContent.taskId || ''),
          flowType: String(formatContent.flowType || ''),
          nodeId: String(formatContent.taskDefinitionKey || ''),
          flowId: String(flowId),
          formModuleCode: String(formatContent.formModuleCode || ''),
          formPageCode: String(formatContent.formPageCode || ''),
          flowTitle,
        },
      });
      return true;
    }
    router.push({
      path: '/flowList/simplFlowDeal',
      query: {
        recordId: String(flowId),
        tableCode: String(tableCode),
        formDataId: String(formDataId),
        pageFrom: 'haveDone',
        flowTitle,
      },
    });
    return true;
  };

  /**
   * 界面页签：/vue3Des/showPage/{pageCode}，页签名取配置标题（页签系统按 fullPath 去重，
   * query 携带消息关联单据标识，不同单据各开一个页签）
   */
  const openDesPageTab = function (record: any, cfg: any) {
    const formatContent = (record && record.formatContent) || {};
    StorageUtils.addSessionItem('curMenuName', cfg.winTitle || cfg.pageName || getMsgTitle(record));
    const query: Record<string, string> = {};
    if (formatContent.id) {
      query.formDataId = String(formatContent.id);
    }
    if (formatContent.flowId) {
      query.flowId = String(formatContent.flowId);
    }
    if (formatContent.tableCode) {
      query.tableCode = String(formatContent.tableCode);
    }
    router.push({path: '/vue3Des/showPage/' + cfg.pageCode, query});
  };

  /**
   * 部件抽屉/窗口：按配置 pageCode+moduleCode 打开（openType=newWindow 弹窗口，其余弹抽屉）
   */
  const openModuleWin = function (record: any, cfg: any) {
    const formatContent = (record && record.formatContent) || {};
    openSubComPage({
      pageCode: cfg.pageCode,
      moduleCode: cfg.moduleCode,
      winType: cfg.openType === 'newWindow' ? 'user-def-win' : 'user-def-drawer',
      winWidth: cfg.winWidth || 1000,
      winHeight: cfg.winHeight || 500,
      winTitle: cfg.winTitle || getMsgTitle(record),
      contentFrom: '',
      contentKey: '',
      inParams: {record: formatContent, recordFrom: 'msgRecord'},
    });
  };

  /**
   * 外部链接：弹出窗口/抽屉内嵌 iframe（复用消息详情弹窗组件，pathUrl 指向配置链接）
   */
  const openPageUrlWin = function (record: any, cfg: any) {
    winTemConfig.value = {...cfg, pathUrl: cfg.pageUrl};
    msgDetailFlag.value = true;
  };

  /**
   * 按消息定义的打开配置打开界面
   * @param record 收件箱消息原始记录（含 messageSetting.tempConfigs、formatContent）
   * @param options skipFlowForm 待办类消息跳过 flowForm 分支（默认待办口径已是可办理表单，不降为只读）
   * @returns false 表示未配置/配置不可执行/流程数据不足，调用方继续走默认逻辑
   */
  const openMsgByCfg = function (record: any, options: {skipFlowForm?: boolean} = {}): boolean {
    if (!record) {
      return false;
    }
    const cfg = parseMsgOpenCfg(record);
    if (!cfg) {
      return false;
    }
    // 内容来源优先：流程表单/界面/部件/外部链接
    if (hasContentSource(cfg)) {
      if (cfg.pageFrom === 'flowForm') {
        if (options.skipFlowForm) {
          return false;
        }
        return openFlowFormTab(record, cfg);
      }
      if (cfg.pageFrom === 'page') {
        openDesPageTab(record, cfg);
        return true;
      }
      if (cfg.pageFrom === 'module') {
        openModuleWin(record, cfg);
        return true;
      }
      if (cfg.pageFrom === 'pageUrl') {
        openPageUrlWin(record, cfg);
        return true;
      }
    }
    // 打开方式兜底：路由跳转（跳转地址）/ 不可点击
    if (cfg.openType === 'routerPath' && cfg.pathUrl) {
      router.push({path: cfg.pathUrl});
      return true;
    }
    if (cfg.openType === 'notClick') {
      return true;
    }
    return false;
  };

  return {openMsgByCfg, getMsgTitle};
}
